/**
 * Role IDs mirrored from the backend RoleIDs constants
 * (GratShare.AdminData/Model/RoleIDs.cs). Used for client-side role gating off
 * Session.roleIds (populated from the login response's RoleIds collection).
 */
export enum RoleIds {
  Admin = 1,
  Sales = 2,
  GratSyncUser = 3,
  Finance = 8,
  Owner = 11
}

/**
 * Config for who may see AI-Insights. Semantics: an EMPTY collection = enabled for
 * ALL roles (no restriction); a non-empty collection = only those roles. This keeps
 * the config lever so we can re-restrict later by re-populating the array, without
 * touching any call site. Gated everywhere via {@link isInsightsRoleAllowed} — the
 * access guard, the header trigger (app.component `canSeeInsights`) and the insights
 * default-landing override (security.helper).
 *
 * 2026-07-29: opened to ALL PDP roles — collection emptied (was Admin/Sales/GratSyncUser).
 */
export const INSIGHTS_ROLE_IDS: number[] = [];

/**
 * True when a user holding <paramref name="roleIds"/> may see AI-Insights.
 * EMPTY {@link INSIGHTS_ROLE_IDS} ⇒ allowed for everyone; otherwise only those roles.
 */
export function isInsightsRoleAllowed(roleIds: number[] | null | undefined): boolean {
  if (!INSIGHTS_ROLE_IDS || INSIGHTS_ROLE_IDS.length === 0) return true; // empty config → all roles
  return (roleIds || []).some(id => INSIGHTS_ROLE_IDS.includes(id));
}

/**
 * Roles allowed to run the TabaPay reconciliation "Download AI analysis" export.
 * Same lever semantics as INSIGHTS_ROLE_IDS: EMPTY ⇒ all roles; non-empty ⇒ only those roles.
 * Enforced for real server-side (PDAPTabapayReconAnalysisController); this only hides the button.
 */
export const TABAPAY_AI_ROLE_IDS: number[] = [RoleIds.Finance];

export function isTabapayAiRoleAllowed(roleIds: number[] | null | undefined): boolean {
  if (!TABAPAY_AI_ROLE_IDS || TABAPAY_AI_ROLE_IDS.length === 0) return true;
  return (roleIds || []).some(id => TABAPAY_AI_ROLE_IDS.includes(id));
}
